import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import { useReducer, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "@entry/navigation";
import styled from "styled-components";

import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import {
  DateFormat,
  Flow,
  Routes,
  SetUserIdentityMutationVariables,
  UserMaritalStatus,
} from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import { entryColors, entryFont } from "@entry/tokens";
import { EntryGlassOption, GlassInput } from "@entry/ui";
import {
  useErrorBanner,
  useOnboardingNextNavigation,
  useGoalFlow,
  useModal,
  useOnboarding,
  useSetUserIdentity,
} from "@hooks";
import { i18n, Translations } from "@i18n";
import { validationSchema } from "./content";
import { MaritalStatusInfoModal } from "./MaritalStatusInfoModal";
import {
  MaritalStatusAction,
  MaritalStatusFormValues,
  MaritalStatusState,
  MaritalStatusType,
} from "./types";

const maximumDate = dayjs().subtract(18, "years").format("YYYY-MM-DD");
const minimumDate = dayjs().subtract(120, "years").format("YYYY-MM-DD");

const initialState: MaritalStatusState = {
  single: false,
  married: false,
};

const reducer = (state: MaritalStatusState, action: MaritalStatusAction) => {
  return { ...initialState, [action.type]: !state[action.type] };
};

const Subtitle = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 15px;
  line-height: 1.5;
  color: ${entryColors.textSecondary};
`;

const LearnMoreLink = styled.span`
  font-weight: 600;
  cursor: pointer;
  color: ${entryColors.highlight};

  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.p`
  margin: 0;
  font-family: ${entryFont.body};
  font-size: 11px;
  line-height: 13px;
  color: rgba(255, 255, 255, 0.6);
  text-align: center;
`;

export const MaritalStatusRoute = () => {
  const [{ single, married }, dispatch] = useReducer(reducer, initialState);

  const [dateStr, setDateStr] = useState("");
  const [continueLoading, setContinueLoading] = useState(false);
  const [skipLoading, setSkipLoading] = useState(false);

  const [setUserIdentity] = useSetUserIdentity();
  const { handleNext } = useOnboardingNextNavigation();
  const { resetToNext } = useOnboarding();
  const navigate = useNavigate();
  const {
    goalProps: { flow },
  } = useGoalFlow();

  const { showErrorBanner } = useErrorBanner();

  const {
    register,
    setValue,
    watch,
    formState: { errors, isValid },
  } = useForm<MaritalStatusFormValues>({
    defaultValues: {
      firstName: "",
      lastName: "",
    },
    mode: "all",
    resolver: yupResolver(validationSchema),
  });

  const firstNameController = register("firstName");
  const lastNameController = register("lastName");

  const handleNavigateNext = () => {
    if (flow === Flow.AddGoal || flow === Flow.Accounts) {
      navigate(Routes.Accounts, { replace: true });
      return;
    }

    if (flow === Flow.AcceptSharedGoal) {
      navigate(Routes.Dashboard, { replace: true });
      return;
    }

    if (flow === Flow.Onboarding || !flow) {
      resetToNext();
      return;
    }

    handleNext();
  };

  const handleSubmit = async () => {
    setContinueLoading(true);

    const variables: SetUserIdentityMutationVariables = married
      ? {
          maritalStatus: UserMaritalStatus.Married,
          spouseFirstName: watch("firstName"),
          spouseLastName: watch("lastName"),
          spouseDateOfBirth: dayjs(dateStr).format(DateFormat.User),
        }
      : {
          maritalStatus: UserMaritalStatus.Single,
        };

    try {
      await setUserIdentity({ variables });
      handleNavigateNext();
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    } finally {
      setContinueLoading(false);
    }
  };

  const handleSkip = async () => {
    setSkipLoading(true);

    try {
      await setUserIdentity({
        variables: { maritalStatus: UserMaritalStatus.Single },
      });
      handleNavigateNext();
    } catch (e: any) {
      const message = e.graphQLErrors?.[0]?.message;
      showErrorBanner(message);
    } finally {
      setSkipLoading(false);
    }
  };

  const disabled = (() => {
    if (skipLoading || continueLoading) return true;
    if (!single && !married) return true;
    if (married && (!dateStr || !isValid)) return true;
    return false;
  })();

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.MaritalStatusRouteHeader),
    footer: (
      <Footer>{i18n.t(Translations.MaritalStatusRouteFooter)}</Footer>
    ),
    cta: {
      primary: {
        title: i18n.t(Translations.MaritalStatusRoutePrimaryCTA),
        onClick: handleSubmit,
        loading: continueLoading,
        disabled,
      },
      secondary: {
        title: i18n.t(Translations.MaritalStatusRouteSecondaryCTA),
        onClick: handleSkip,
        loading: skipLoading,
      },
    },
  };

  const { show } = useModal();

  return (
    <OnboardingLayout {...props}>
      <Subtitle>
        {i18n.t(Translations.MaritalStatusRouteSubtitle)}{" "}
        <LearnMoreLink onClick={() => show(<MaritalStatusInfoModal />)}>
          {i18n.t(Translations.MaritalStatusRouteSubtitleHighlight)}
        </LearnMoreLink>
      </Subtitle>
      <Spacing size={SpacingSizes.M} />

      <EntryGlassOption
        title={i18n.t(Translations.MaritalStatusRouteOption1Title)}
        selected={single}
        onSelect={() => dispatch({ type: MaritalStatusType.Single })}
      />
      <Spacing size={SpacingSizes.S} />

      <EntryGlassOption
        title={i18n.t(Translations.MaritalStatusRouteOption2Title)}
        selected={married}
        onSelect={() => dispatch({ type: MaritalStatusType.Married })}
      />
      <Spacing size={SpacingSizes.XL} />

      {married ? (
        <>
          <GlassInput
            {...firstNameController}
            label={i18n.t(Translations.MaritalStatusRouteSpouseFirstNameInput)}
            value={watch("firstName")}
            onChange={(e) => setValue("firstName", e.target.value, { shouldValidate: true })}
            error={errors.firstName?.message}
            placeholder={i18n.t(Translations.MaritalStatusRouteSpouseFirstNamePlaceholder)}
          />
          <Spacing size={SpacingSizes.M} />

          <GlassInput
            {...lastNameController}
            label={i18n.t(Translations.MaritalStatusRouteSpouseLastNameInput)}
            value={watch("lastName")}
            onChange={(e) => setValue("lastName", e.target.value, { shouldValidate: true })}
            error={errors.lastName?.message}
            placeholder={i18n.t(Translations.MaritalStatusRouteSpouseLastNamePlaceholder)}
          />
          <Spacing size={SpacingSizes.M} />

          <GlassInput
            type="date"
            label={i18n.t(Translations.MaritalStatusRouteSpouseDateOfBirthInput)}
            value={dateStr}
            onChange={(e) => setDateStr(e.target.value)}
            min={minimumDate}
            max={maximumDate}
          />
          <Spacing size={SpacingSizes.M} />
        </>
      ) : null}
    </OnboardingLayout>
  );
};
